import React, {useEffect, useState} from "react";
import Card from "components/Cards/Card";
import ToggleButton from "components/ui/ToggleButton";
import keyQuestionsIcon from "assets/Icon_Tab_10.png";
import {setChartSelection, getChartSelection} from "helpers/chartSelections";
import {v4 as uuidv4} from "uuid";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {useCurrentPng} from "recharts-to-png";
import {ImageMap} from "../../Dashboard/Dashboard";
import reviewSummaryIcon from "../../../assets/Icon_Tab_08.png";
import LoadingIndicator from "../../ui/LoadingIndicator";
import PortalTooltip from "../PortalTooltip";

type KeyQsProps = {
  href: string | null;
  data: ChartData;
  minWidth: string;
  defaultChartType: string;
  customName?: string;
  setImages?: React.Dispatch<React.SetStateAction<ImageMap>>;
  idx?: number;
  isLoading?: boolean; // Add loading prop
};

type ChartData = {
  pieChartData: any;
  otherChartData: any[];
  options: {
    yAxisType: "Count" | "Percentage" | "Score";
  };
  questions?: string[];
  periods?: string[];
};

const chartColours = [
  "#307bb9",
  "#30b862",
  "#a440a4",
  "#d5a438",
  "#bb4040",
  "#008272",
  "#4068b4",
  "#7ebce6",
  "#8fba34",
  "#f64250",
  "#fbbf22",
  "#94adbf",
];

const checkNullChartData = (c: any[]) => {
  const data = c.find((v) => v);
  return !!data;
};

function percentageFormatter(item: any) {
  return parseFloat(item).toFixed(1) + "%";
}

const Tick: React.FC<any> = ({
  payload: { value },
  verticalAnchor,
  visibleTicksCount,
  ...rest
}) => (
  <Text
    {...rest}
    verticalAnchor={verticalAnchor}
    visibleTicksCount={visibleTicksCount}
    width={80}
    fontSize={11}
  >
    {value}
  </Text>
);

const ticks20 = [0, 20, 40, 60, 80, 100];

function getMinValue(data: any[], keys?: string[]): number {
  let min = 0;
  if (!data || !data.length) return 0;
  data.forEach((item: any) => {
    if (keys && keys.length) {
      keys.forEach((k) => {
        const v = item[k];
        if (typeof v === "number" && v < min) min = v;
      });
    } else {
      Object.entries(item).forEach(([k, v]) => {
        if (k !== "name" && typeof v === "number" && (v as number) < min) min = v as number;
      });
    }
  });
  return min;
}

function getPercentageDomain(data: any[], keys?: string[]): [number, number] {
  const min = getMinValue(data, keys);
  if (min >= 0) return [0, 100];
  const roundedMin = Math.floor(min / 20) * 20;
  return [Math.max(roundedMin, -100), 100];
}

function getPercentageTicks(data: any[], keys?: string[]): number[] {
  const min = getMinValue(data, keys);
  if (min >= 0) return ticks20;
  const roundedMin = Math.floor(min / 20) * 20;
  const start = Math.max(roundedMin, -100);
  const ticks: number[] = [];
  for (let i = start; i <= 100; i += 20) {
    ticks.push(i);
  }
  return ticks;
}

function scoreFormatter(item: any) {
  return parseFloat(item).toFixed(2);
}

const scoreTicks = [0, 1, 2, 3, 4, 5];
const scoreDomain: [number, number] = [0, 5];

function getYAxisFormatter(yAxisType: string | undefined) {
  if (yAxisType === "Score") return scoreFormatter;
  return percentageFormatter;
}

function getYAxisDomain(yAxisType: string | undefined, data: any[], keys?: string[]) {
  if (yAxisType === "Score") return scoreDomain;
  return getPercentageDomain(data, keys);
}

function getYAxisTicks(yAxisType: string | undefined, data: any[], keys?: string[]) {
  if (yAxisType === "Score") return scoreTicks;
  return getPercentageTicks(data, keys);
}

function getValueFormatter(yAxisType: string | undefined) {
  if (yAxisType === "Score") return scoreFormatter;
  return percentageFormatter;
}

const KeyQuestions: React.FC<KeyQsProps> = ({
  href,
  data,
  minWidth,
  defaultChartType,
  customName,
  setImages,
  idx,
    isLoading = true
}) => {
  const chartTypeId = `keyQuestions_chartType_${customName || "default"}`;
  const togglesId = `keyQuestions_toggles_${customName || "default"}`;

  const [keyQuestionsChartType, setKeyQuestionsChartType] = useState(
    () => getChartSelection(chartTypeId) || defaultChartType || "Area"
  );

  const handleSetKeyQuestionsChartType = (value: string) => {
    setKeyQuestionsChartType(value);
    setChartSelection(chartTypeId, value);
  };

  const [keyQuestionsPeriodToggles, setKeyQuestionsPeriodToggles] = useState<
    Record<string, boolean>
  >({});

  // Track internal loading state
  const [internalLoading, setInternalLoading] = useState(true);

  useEffect(() => {
    //let counter = 1;
    const periods: Record<string, boolean> = {};
    if (data && data.periods) {
      for (const period of data.periods) {
        // only set the first toggle to true
        //periods[period] = counter === 1;
        periods[period] = true;
        //counter++;
      }
    }
    const storedToggles = getChartSelection(togglesId);
    if (storedToggles && typeof storedToggles === "object") {
      // Only apply stored toggles for keys that exist in current data
      const merged: Record<string, boolean> = {};
      for (const key of Object.keys(periods)) {
        merged[key] = storedToggles.hasOwnProperty(key) ? storedToggles[key] : periods[key];
      }
      setKeyQuestionsPeriodToggles(merged);
      setChartSelection(togglesId, merged);
    } else {
      setKeyQuestionsPeriodToggles(periods);
      setChartSelection(togglesId, periods);
    }
  }, [data]);

  // Effect to check if data is ready
  useEffect(() => {
    if (data && data.otherChartData) {
      // Set a small delay to prevent flashing
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [data]);

  const handleKeyQuestionsPeriodToggle = (t: string) => {
    setKeyQuestionsPeriodToggles((keyQuestionsPeriodToggles) => {
      const toggles = { ...keyQuestionsPeriodToggles };
      toggles[t] = !toggles[t];
      setChartSelection(togglesId, toggles);
      return toggles;
    });
  };

  // Listen for sync event from PDF generator
  useEffect(() => {
    const handleSync = () => {
      const storedChartType = getChartSelection(chartTypeId);
      if (storedChartType) setKeyQuestionsChartType(storedChartType);
      const storedToggles = getChartSelection(togglesId);
      if (storedToggles) setKeyQuestionsPeriodToggles(storedToggles);
    };
    window.addEventListener("pdf-sync-selections", handleSync);
    return () => window.removeEventListener("pdf-sync-selections", handleSync);
  }, [chartTypeId, togglesId]);

  // Determine if we should show loading state
  const showLoading = isLoading || internalLoading;

  const uuid = uuidv4();

  const [getPng, { ref }] = useCurrentPng();
  const [savedPng, setSavedPng] = useState<boolean>(false);

  async function saveChart() {
    let runningInterval = setInterval(async () => {
      if (savedPng) {
        clearInterval(runningInterval);
      }
      try {
        const png = await getPng();

        // Verify that png is not undefined
        // console.log("runchart");
        if (png && setImages && idx) {
          // console.log("savechart");
          setImages((oldState: ImageMap) => {
            return {...oldState, [idx]: png}
          });
          setSavedPng(true);
          clearInterval(runningInterval);
        }
      }
      catch(error) {
        // console.log(error)
      }
    }, 1000)
  }

  /*useEffect(() => {
    if (!savedPng) {
      saveChart();
    }
  }, [savedPng, data])*/
  if (showLoading) {
    return (
        <Card
            href={href}
            icon={keyQuestionsIcon}
            title={customName ? customName : "KEY QUESTIONS"}
            chartOptions={
              <React.Fragment>
                <button
                    className="Option"
                    onClick={() => handleSetKeyQuestionsChartType("Area")}
                >
                  Area
                </button>
                <button
                    className="Option"
                    onClick={() => handleSetKeyQuestionsChartType("Bar")}
                >
                  Bar
                </button>
                <button
                    className="Option"
                    onClick={() => handleSetKeyQuestionsChartType("Line")}
                >
                  Line
                </button>
                {/* <button
            className="Option"
            onClick={() => handleSetKeyQuestionsChartType("Pie")}
          >
            Pie
          </button> */}
              </React.Fragment>
            }
        >
          <div className="CardBody">
            <div className="ToggleFlex">
              <div className="BarToggle">
                {keyQuestionsPeriodToggles &&
                    Object.entries(keyQuestionsPeriodToggles).map(
                        ([key, value], index: number) => {
                          return (
                              <ToggleButton
                                  key={index}
                                  active={value}
                                  toggle={() => handleKeyQuestionsPeriodToggle(key)}
                              >
                                <div
                                    className="ToggleDot"
                                    style={{
                                      backgroundColor: chartColours[index],
                                    }}
                                ></div>
                                <div className="ToggleLabel">{key}</div>
                              </ToggleButton>
                          );
                        }
                    )}
              </div>
            </div>
            <div className="Chart" style={{height: "400px", overflow: "visible"}}>
              <LoadingIndicator/>
            </div>
          </div>
        </Card>
    );
  }
  return (
      <Card
          href={href}
          icon={keyQuestionsIcon}
          title={customName ? customName : "KEY QUESTIONS"}
          chartOptions={
            <React.Fragment>
              <button
                  className="Option"
                  onClick={() => handleSetKeyQuestionsChartType("Area")}
              >
                Area
              </button>
              <button
                  className="Option"
                  onClick={() => handleSetKeyQuestionsChartType("Bar")}
              >
                Bar
              </button>
              <button
                  className="Option"
                  onClick={() => handleSetKeyQuestionsChartType("Line")}
              >
                Line
              </button>
              {/* <button
            className="Option"
            onClick={() => handleSetKeyQuestionsChartType("Pie")}
          >
            Pie
          </button> */}
            </React.Fragment>
          }
      >
        <div className="CardBody" >
        <div className="ToggleFlex">
          <div className="BarToggle">
            {keyQuestionsPeriodToggles &&
              Object.entries(keyQuestionsPeriodToggles).map(
                ([key, value], index: number) => {
                  return (
                    <ToggleButton
                      key={index}
                      active={value}
                      toggle={() => handleKeyQuestionsPeriodToggle(key)}
                    >
                      <div
                        className="ToggleDot"
                        style={{
                          backgroundColor: chartColours[index],
                        }}
                      ></div>
                      <div className="ToggleLabel">{key}</div>
                    </ToggleButton>
                  );
                }
              )}
          </div>
        </div>
        <div className="Chart"  style={{height: "400px", overflow: "visible"}}>
          <div className="ChartWrapper">
            {data && checkNullChartData(data.pieChartData.data) ? (
              <div className={Math.max(Number(minWidth), (data.otherChartData?.length || 0) * 120) > 400 ? "scrollable-chart" : undefined} style={{ minWidth: `${Math.max(Number(minWidth), (data.otherChartData?.length || 0) * 120)}px` }}>
                <ResponsiveContainer width="99%" height={380} key={uuid}>
                  {keyQuestionsChartType === "Area" ? (
                    <AreaChart
                      data={data.otherChartData}
                      margin={{ top: 20, bottom: 50, right: 20 }}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={
                          data.options.yAxisType === "Percentage" || data.options.yAxisType === "Score"
                            ? getValueFormatter(data.options.yAxisType)
                            : undefined
                        } />} />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        padding={{ left: 100, right: 100 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={getYAxisFormatter(data.options.yAxisType)}
                        domain={getYAxisDomain(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                        ticks={getYAxisTicks(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                      />
                      {keyQuestionsPeriodToggles &&
                        Object.entries(keyQuestionsPeriodToggles).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Area
                                  key={index}
                                  dataKey={key}
                                  fill={chartColours[index]}
                                  isAnimationActive={false}
                                  type="monotone"
                                  stroke={chartColours[index]}
                                  dot={true}
                                  fillOpacity=".1"
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={getValueFormatter(data.options.yAxisType)}
                                  />
                                </Area>
                              );
                            } else {
                              return <React.Fragment />;
                            }
                          }
                        )}
                    </AreaChart>
                  ) : keyQuestionsChartType === "Bar" ? (
                    <BarChart
                      data={data.otherChartData}
                      maxBarSize={65}
                      barGap={15}
                      barCategoryGap={10}
                      margin={{ top: 20, bottom: 50, right: 20 }}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={
                          data.options.yAxisType === "Percentage" || data.options.yAxisType === "Score"
                            ? getValueFormatter(data.options.yAxisType)
                            : undefined
                        } />} />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={getYAxisFormatter(data.options.yAxisType)}
                        domain={getYAxisDomain(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                        ticks={getYAxisTicks(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                      />
                      {keyQuestionsPeriodToggles &&
                        Object.entries(keyQuestionsPeriodToggles).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Bar
                                  key={index}
                                  dataKey={key}
                                  fill={chartColours[index]}
                                  radius={[3, 3, 0, 0]}
                                  isAnimationActive={false}
                                  // shape={({ x, y, width, height, fill }) => {
                                  //   return (
                                  //     <rect
                                  //       x={x}
                                  //       y={y}
                                  //       width={width}
                                  //       height={height}
                                  //     />
                                  //   );
                                  // }}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={getValueFormatter(data.options.yAxisType)}
                                  />
                                </Bar>
                              );
                            } else {
                              return <React.Fragment />;
                            }
                          }
                        )}
                    </BarChart>
                  ) : keyQuestionsChartType === "Line" ? (
                    <LineChart
                      data={data.otherChartData}
                      margin={{ top: 20, bottom: 50, right: 20 }}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={
                          data.options.yAxisType === "Percentage" || data.options.yAxisType === "Score"
                            ? getValueFormatter(data.options.yAxisType)
                            : undefined
                        } />} />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        padding={{ left: 100, right: 100 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={getYAxisFormatter(data.options.yAxisType)}
                        domain={getYAxisDomain(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                        ticks={getYAxisTicks(data.options.yAxisType, data.otherChartData, keyQuestionsPeriodToggles ? Object.entries(keyQuestionsPeriodToggles).filter(([, v]) => v).map(([k]) => k) : undefined)}
                      />
                      {keyQuestionsPeriodToggles &&
                        Object.entries(keyQuestionsPeriodToggles).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Line
                                  key={index}
                                  dataKey={key}
                                  fill={chartColours[index]}
                                  isAnimationActive={false}
                                  type="monotone"
                                  stroke={chartColours[index]}
                                  dot={{
                                    stroke: chartColours[index],
                                    strokeWidth: 2,
                                  }}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={getValueFormatter(data.options.yAxisType)}
                                  />
                                </Line>
                              );
                            } else {
                              return <React.Fragment />;
                            }
                          }
                        )}
                    </LineChart>
                  ) : keyQuestionsChartType === "Pie" ? (
                    <div className="PieFlex">
                      {data.pieChartData.data.map((p: any, i: number) => {
                        const noData = "No data available";
                        let newP: any = [];
                        if (p) {
                          for (const dataPoint of p) {
                            const newPoint = { ...dataPoint };
                            if (!keyQuestionsPeriodToggles[newPoint.label]) {
                              newPoint.count = 0;
                            }

                            newP.push(newPoint);
                          }
                        } else newP = null;
                        return (
                          <div key={i}>
                            {newP ? (
                              <PieChart width={270} height={220} ref={ref}>
                                <Pie
                                  data={newP}
                                  dataKey="count"
                                  nameKey="label"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={80}
                                  legendType="circle"
                                  stroke="none"
                                  label={({ value }) => {
                                    if (!isNaN(value) && value !== 0) {
                                      return `${value}%`;
                                    } else {
                                      return "";
                                    }
                                  }}
                                  labelLine={false}
                                  isAnimationActive={false}
                                >
                                  {chartColours.map(
                                    (colour: string, index: number) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={colour}
                                      />
                                    )
                                  )}
                                </Pie>
                                <Tooltip content={<PortalTooltip formatter={
                                    data.options.yAxisType === "Percentage" || data.options.yAxisType === "Score"
                                      ? getValueFormatter(data.options.yAxisType)
                                      : undefined
                                  } />} />
                              </PieChart>
                            ) : (
                              <PieChart width={250} height={220} ref={ref}>
                                <text
                                  x="50%"
                                  y="50%"
                                  textAnchor="middle"
                                  fontSize={14}
                                  fontFamily="Arial"
                                  fontWeight={400}
                                >
                                  {noData}
                                </text>
                              </PieChart>
                            )}
                            <div
                              style={{
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  margin: "10px auto",
                                  display: "block",
                                  width: "200px",
                                }}
                              >
                                {data ? data.pieChartData.questions[i] : ""}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <React.Fragment />
                  )}
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="NoChartData">
                <p>No results found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default KeyQuestions;
